/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Observable } from 'rxjs';
import { PreviewEnvironment } from 'app/shared-data/models';
import { SharedDataService } from './shared-data.service';
import { observableThrow } from 'app/shared/rxjs/rxjs.utils';
import { Injectable } from '@angular/core';
import { RestClientService } from 'app/shared/services/rest-client.service';
import { Location } from '@angular/common';
import { environment } from 'environments/environment';
import { map, catchError } from 'rxjs/operators';
import { TenantDetails } from 'app/page-modules/tenant-management/models';
import { DateTime } from 'luxon';
import { DateTimeToString } from 'app/shared/luxon/datetime-parsing';

/** Returns data from the API. */
@Injectable()
export class ApiSharedDataService extends SharedDataService {
  /**
   * Constructs an ApiSharedDataService instance
   * @param client the rest client instance to use
   */
  constructor(private client: RestClientService) {
    super();
  }

  /** Returns available preview environment data. */
  previewEnvironments(): Observable<PreviewEnvironment[]> {
    return this.client
      .get<DateTimeToString<PreviewEnvironment[]>>(
        Location.joinWithSlash(environment.baseUrl, 'system/preview-environments')
      )
      .pipe(
        map(res => res.body!),
        map(environments => environments.map(env => this.mapDateTimes(env))),
        catchError(() => observableThrow('Error loading preview environments'))
      );
  }

  tenants(): Observable<TenantDetails[]> {
    return this.client
      .get<TenantDetails[]>(Location.joinWithSlash(environment.baseUrl, 'tenants'))
      .pipe(
        map(res => res.body! as Partial<TenantDetails>[]),
        map(tenants =>
          tenants.map(
            tenant =>
              ({
                ...tenant,
                status: tenant.status || 'enabled'
              } as TenantDetails)
          )
        ),
        catchError(() => observableThrow('Error loading tenants'))
      );
  }

  // -------------------------------------------------------------------------
  private mapDateTimes(result: DateTimeToString<PreviewEnvironment>): PreviewEnvironment {
    return {
      ...result,
      modificationInstant: DateTime.fromISO(result.modificationInstant)
    };
  }
}
